// src/components/PublicCompetitions.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Dialog,
    Button,
    Chip,
    Paper,
    Divider,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CalendarToday, LocationOn, People, EmojiEvents } from '@mui/icons-material';
import { getPublicCompetitions } from '../api/competitionApi';
import PublicCompetitionView from './PublicCompetitionView';

export default function PublicCompetitions({ affiliateId, userId }) {
    const [competitions, setCompetitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [showPassed, setShowPassed] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        loadCompetitions();
    }, [affiliateId]);

    const loadCompetitions = async () => {
        try {
            setLoading(true);
            const data = await getPublicCompetitions(affiliateId);
            setCompetitions(data);
        } catch (error) {
            console.error('Error loading competitions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCompetitionClick = (competition) => {
        setSelectedCompetition(competition);
        setOpenDialog(true);
    };

    const formatDate = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        const startStr = start.toLocaleDateString('et-EE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        // Check if same day
        if (start.toDateString() === end.toDateString()) {
            return startStr;
        }

        const endStr = end.toLocaleDateString('et-EE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        return `${startStr} - ${endStr}`;
    };

    const isOngoing = (startDate, endDate) => {
        const now = new Date();
        return new Date(startDate) <= now && new Date(endDate) >= now;
    };

    const isPassed = (endDate) => {
        const now = new Date();
        const end = new Date(endDate);
        const daysDiff = Math.floor((now - end) / (1000 * 60 * 60 * 24));
        return daysDiff >= 7;
    };

    const isUpcoming = (startDate) => {
        return new Date(startDate) > new Date();
    };

    // Filter competitions
    const activeCompetitions = competitions.filter(c => !isPassed(c.endDate));
    const passedCompetitions = competitions.filter(c => isPassed(c.endDate));
    const displayedCompetitions = showPassed ? passedCompetitions : activeCompetitions;

    const CompetitionItem = ({ competition }) => {
        const ongoing = isOngoing(competition.startDate, competition.endDate);
        const upcoming = isUpcoming(competition.startDate);
        const passed = isPassed(competition.endDate);

        return (
            <Paper
                sx={{
                    p: 2,
                    mb: 1.5,
                    cursor: 'pointer',
                    opacity: passed ? 0.7 : 1,
                    '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s'
                    }
                }}
                onClick={() => handleCompetitionClick(competition)}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        {/* Competition Name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmojiEvents sx={{ color: 'primary.main', fontSize: 24 }} />
                            <Typography variant="h6" fontWeight="bold">
                                {competition.name}
                            </Typography>
                            {ongoing && (
                                <Chip
                                    label="ONGOING"
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            )}
                            {upcoming && (
                                <Chip
                                    label="UPCOMING"
                                    color="info"
                                    size="small"
                                />
                            )}
                            {passed && (
                                <Chip
                                    label="PASSED"
                                    size="small"
                                    sx={{ bgcolor: 'grey.400', color: 'white' }}
                                />
                            )}
                        </Box>

                        {/* Date and Location */}
                        <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {formatDate(competition.startDate, competition.endDate)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {competition.location}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Registration Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <People sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {competition._count.registrations}/{competition.maxParticipants} registered
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                •
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                {competition.isTeamCompetition
                                    ? `Team (${competition.teamSize} members)`
                                    : 'Individual'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                •
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                {competition._count.workouts} workouts
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                    Competitions
                </Typography>

                {passedCompetitions.length > 0 && (
                    <Button
                        variant={showPassed ? 'contained' : 'outlined'}
                        onClick={() => setShowPassed(!showPassed)}
                    >
                        {showPassed ? 'Show Active' : `Passed Competitions (${passedCompetitions.length})`}
                    </Button>
                )}
            </Box>

            {loading ? (
                <Typography>Loading competitions...</Typography>
            ) : displayedCompetitions.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography align="center" color="textSecondary">
                        {showPassed
                            ? 'No passed competitions'
                            : 'No competitions available at the moment'}
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {displayedCompetitions.map((competition) => (
                        <CompetitionItem key={competition.id} competition={competition} />
                    ))}
                </Box>
            )}

            {/* Competition Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="lg"
                fullWidth
                fullScreen={fullScreen}
            >
                {selectedCompetition && (
                    <PublicCompetitionView
                        competitionId={selectedCompetition.id}
                        userId={userId}
                        onClose={() => {
                            setOpenDialog(false);
                            loadCompetitions();
                        }}
                    />
                )}
            </Dialog>
        </Box>
    );
}